import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, List, User } from 'lucide-react';
import {
  appointmentsService,
  type CreateAppointmentData,
  type UpdateAppointmentData,
} from '@/services/api/appointments.service';
import { patientsService, type PatientWithDoctors } from '@/services/api/patients.service';
import type { Appointment } from '@/types';
import { CalendarDayView } from '@/components/calendar/CalendarDayView';
import { CalendarWeekView } from '@/components/calendar/CalendarWeekView';
import { PatientSearchModal } from '@/components/calendar/PatientSearchModal';
import { PatientInfoPanel } from '@/components/calendar/PatientInfoPanel';
import { ConsultationModal } from '@/components/calendar/ConsultationModal';
import { ConsultationDetailsModal } from '@/components/calendar/ConsultationDetailsModal';
import { CancelRescheduleDialog } from '@/components/calendar/CancelRescheduleDialog';
import { useContext } from '@/hooks/useContext';

type ViewMode = 'list' | 'day' | 'week';

function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { doctorId, doctor } = useContext();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<PatientWithDoctors[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<PatientWithDoctors | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Date filters for list view only
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  useEffect(() => {
    loadPatients();
  }, [doctorId]);

  useEffect(() => {
    if (doctorId) {
      loadAppointments();
    }
  }, [page, dateFrom, dateTo, viewMode, currentDate, doctorId]);

  // Check for ?new=true to open create modal
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      handleCreate();
      // Remove the query param
      setSearchParams({});
    }
  }, [searchParams]);

  const loadAppointments = async () => {
    if (!doctorId) return;

    try {
      setLoading(true);
      const params: any = {
        doctorId, // Always filter by the selected doctor
      };

      // For calendar views, load appointments for the current view period
      if (viewMode === 'day') {
        const startOfDay = new Date(currentDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(currentDate);
        endOfDay.setHours(23, 59, 59, 999);
        params.from = startOfDay.toISOString();
        params.to = endOfDay.toISOString();
        params.limit = 100;
      } else if (viewMode === 'week') {
        const startOfWeek = new Date(currentDate);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        params.from = startOfWeek.toISOString();
        params.to = endOfWeek.toISOString();
        params.limit = 500;
      } else {
        params.page = page;
        params.limit = 10;
        if (dateFrom) params.from = dateFrom;
        if (dateTo) params.to = dateTo;
      }

      const response = await appointmentsService.getAll(params);
      
      // Keep all appointments (including completed ones) visible in calendar
      // They will be differentiated by color and status badges
      setAppointments(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await patientsService.getAll({ limit: 1000 });
      // Filter patients by the currently selected doctor
      let filteredPatients = response.data;
      if (doctorId) {
        filteredPatients = response.data.filter((patient) => 
          patient.doctorIds && patient.doctorIds.includes(doctorId)
        );
      }
      setPatients(filteredPatients);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao carregar pacientes');
    }
  };

  const handleCreate = (prefilledDate?: Date) => {
    setSelectedAppointment(
      prefilledDate
        ? ({
            startUtc: prefilledDate.toISOString(),
            endUtc: new Date(prefilledDate.getTime() + 60 * 60 * 1000).toISOString(),
          } as Partial<Appointment> as Appointment)
        : null
    );
    setIsEditing(false);
    setShowFormModal(true);
  };

  const handleEdit = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const handleDelete = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedAppointment) return;

    try {
      await appointmentsService.delete(selectedAppointment.id);
      toast.success('Agendamento excluído com sucesso');
      setShowDeleteDialog(false);
      setSelectedAppointment(null);
      loadAppointments();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao excluir agendamento');
    }
  };

  const handleStartConsultation = async (appointment: Appointment) => {
    if (!appointment.patientId) {
      toast.error('Este agendamento não tem um paciente vinculado');
      return;
    }
    
    // Load patient data to check if has user account
    try {
      // Force fresh data from API (no cache)
      const patient = await patientsService.getById(appointment.patientId);
      console.log('[CalendarPage] Patient loaded for consultation:', {
        patientId: patient.id,
        patientName: patient.fullName,
        userId: patient.userId,
        hasUser: !!patient.userId
      });
      setSelectedPatient(patient);
      setSelectedAppointment(appointment);
      setShowConsultationModal(true);
    } catch (error) {
      console.error('[CalendarPage] Error loading patient:', error);
      toast.error('Erro ao carregar dados do paciente');
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatCurrentPeriod = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
      startOfWeek.setDate(diff);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString('pt-BR')} - ${endOfWeek.toLocaleDateString('pt-BR')}`;
    }
    return '';
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };



  const getPatientName = (patientId?: string | null) => {
    if (!patientId) return '-';
    const patient = patients.find((p) => p.id === patientId);
    return patient?.fullName || 'Paciente não encontrado';
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    // Open edit modal when clicking on an appointment
    handleEdit(appointment);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendário de Agendamentos</h1>
          <p className="text-gray-600 mt-1">
            Médico: <span className="font-semibold">{doctor?.name || 'Não selecionado'}</span>
            {doctor?.specialty && ` - ${doctor.specialty}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleCreate()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Novo Agendamento
          </button>
        </div>
      </div>

      {/* View Mode Selector and Navigation */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List className="w-4 h-4" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'day'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Dia
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                viewMode === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Semana
            </button>
          </div>

          {(viewMode === 'day' || viewMode === 'week') && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goToToday}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                Hoje
              </button>
              <button
                onClick={() => navigateDate('next')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-gray-700 ml-2">
                {formatCurrentPeriod()}
              </span>
            </div>
          )}
        </div>

        {/* Date Filters - Only for list view */}
        {viewMode === 'list' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Final
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                Limpar filtros
              </button>
            )}
          </>
        )}
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {viewMode === 'day' && (
            <CalendarDayView
              date={currentDate}
              appointments={appointments.filter(apt => apt.status !== 'Cancelada')}
              patients={patients}
              onAppointmentClick={handleAppointmentClick}
              onTimeSlotClick={handleCreate}
            />
          )}

          {viewMode === 'week' && (
            <CalendarWeekView
              startDate={currentDate}
              appointments={appointments.filter(apt => apt.status !== 'Cancelada')}
              patients={patients}
              onAppointmentClick={handleAppointmentClick}
              onTimeSlotClick={(date, startTime) => handleCreate(startTime)}
            />
          )}

          {viewMode === 'list' && (
            <>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paciente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                          Nenhum agendamento encontrado
                        </td>
                      </tr>
                    ) : (
                      appointments.map((appointment) => (
                        <tr 
                          key={appointment.id} 
                          className={`hover:bg-gray-50 ${
                            appointment.status === 'Realizada' ? 'bg-green-50' : 
                            appointment.status === 'Cancelada' ? 'bg-red-50' : ''
                          }`}
                        >
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                if (appointment.patientId) {
                                  const patient = patients.find((p) => p.id === appointment.patientId);
                                  if (patient) setSelectedPatient(patient);
                                }
                              }}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            >
                              <User className="w-4 h-4" />
                              {getPatientName(appointment.patientId)}
                            </button>
                            {appointment.description && (
                              <div className="text-xs text-gray-500 mt-1">
                                {appointment.description}
                              </div>
                            )}
                            {appointment.status === 'Realizada' && (
                              <div className="text-xs text-green-600 mt-1 font-medium">
                                ✓ Consulta realizada
                              </div>
                            )}
                            {appointment.status === 'Cancelada' && appointment.cancelReason && (
                              <div className="text-xs text-red-600 mt-1">
                                ✗ Cancelada: {appointment.cancelReason}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDateTime(appointment.startUtc)}
                            </div>
                            <div className="text-sm text-gray-500">
                              até {formatDateTime(appointment.endUtc)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  appointment.isAllDay
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {appointment.isAllDay ? 'Dia inteiro' : 'Horário'}
                              </span>
                              {appointment.source === 'google' && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Google
                                </span>
                              )}
                              {appointment.status === 'Realizada' && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-500 text-white">
                                  Realizada
                                </span>
                              )}
                              {appointment.status === 'Cancelada' && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-500 text-white">
                                  Cancelada
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {appointment.status === 'Realizada' && appointment.patientId && (
                              <button
                                onClick={() => {
                                  setSelectedAppointment(appointment);
                                  setShowDetailsModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                Ver Detalhes
                              </button>
                            )}
                            {appointment.patientId && appointment.status !== 'Realizada' && appointment.status !== 'Cancelada' && (
                              <button
                                onClick={() => handleStartConsultation(appointment)}
                                className="text-green-600 hover:text-green-900 mr-3"
                              >
                                Iniciar Consulta
                              </button>
                            )}
                            {appointment.status !== 'Realizada' && (
                              <button
                                onClick={() => handleEdit(appointment)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                Editar
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(appointment)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Excluir
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <span className="px-4 py-2">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <AppointmentFormModal
          appointment={selectedAppointment}
          isEditing={isEditing}
          patients={patients}
          selectedPatient={selectedPatient}
          currentDoctorId={doctorId}
          currentDoctorName={doctor?.name}
          onClose={() => {
            setShowFormModal(false);
            setSelectedAppointment(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setSelectedAppointment(null);
            loadAppointments();
          }}
          onPatientSearch={() => setShowPatientSearch(true)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedAppointment && (
        <DeleteConfirmDialog
          appointmentTitle={selectedAppointment.title}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setSelectedAppointment(null);
          }}
        />
      )}

      {/* Patient Search Modal */}
      {showPatientSearch && (
        <PatientSearchModal
          onSelect={(patient) => {
            setShowPatientSearch(false);
            setSelectedPatient(patient);
            loadPatients(); // Reload patients to update dropdown
          }}
          onClose={() => setShowPatientSearch(false)}
          currentDoctorId={doctorId}
          currentDoctorName={doctor?.name}
        />
      )}

      {/* Patient Info Panel */}
      {selectedPatient && (
        <PatientInfoPanel
          patient={selectedPatient}
          onClose={() => setSelectedPatient(null)}
        />
      )}

      {/* Consultation Modal */}
      {showConsultationModal && selectedAppointment && selectedAppointment.patientId && (
        <ConsultationModal
          appointment={selectedAppointment}
          patientName={getPatientName(selectedAppointment.patientId)}
          patientHasUser={!!selectedPatient?.userId}
          onClose={() => {
            setShowConsultationModal(false);
            setSelectedAppointment(null);
            setSelectedPatient(null);
          }}
          onSuccess={() => {
            setShowConsultationModal(false);
            setSelectedAppointment(null);
            setSelectedPatient(null);
            loadAppointments();
          }}
        />
      )}

      {/* Consultation Details Modal */}
      {showDetailsModal && selectedAppointment && selectedAppointment.patientId && (
        <ConsultationDetailsModal
          appointment={selectedAppointment}
          patientName={getPatientName(selectedAppointment.patientId)}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAppointment(null);
          }}
          onSuccess={() => {
            loadAppointments();
          }}
        />
      )}
    </div>
  );
}

// Appointment Form Modal Component
interface AppointmentFormModalProps {
  appointment: Appointment | null;
  isEditing: boolean;
  patients: PatientWithDoctors[];
  selectedPatient: PatientWithDoctors | null;
  currentDoctorId: string | null;
  currentDoctorName?: string;
  onClose: () => void;
  onSuccess: () => void;
  onPatientSearch: () => void;
}

function AppointmentFormModal({
  appointment,
  isEditing,
  patients,
  selectedPatient,
  currentDoctorId,
  currentDoctorName,
  onClose,
  onSuccess,
  onPatientSearch,
}: AppointmentFormModalProps) {
  const { clinic } = useContext();
  
  // Parse existing appointment times to local timezone
  const parseLocalDateTime = (isoString?: string) => {
    if (!isoString) return { date: '', hour: '', minute: '' };
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    
    return {
      date: `${year}-${month}-${day}`,
      hour,
      minute,
    };
  };

  const startParsed = parseLocalDateTime(appointment?.startUtc);
  const endParsed = parseLocalDateTime(appointment?.endUtc);

  const [formData, setFormData] = useState<CreateAppointmentData | UpdateAppointmentData>({
    clinicId: appointment?.clinicId || clinic?.id || '',
    doctorId: appointment?.doctorId || currentDoctorId || '',
    patientId: appointment?.patientId || '',
    title: appointment?.title || '',
    description: appointment?.description || '',
    startUtc: appointment?.startUtc || '',
    endUtc: appointment?.endUtc || '',
    isAllDay: appointment?.isAllDay || false,
  });
  
  // Separate state for date/time inputs
  const [startDate, setStartDate] = useState(startParsed.date);
  const [startHour, setStartHour] = useState(startParsed.hour || '09');
  const [startMinute, setStartMinute] = useState(startParsed.minute || '00');
  const [endDate, setEndDate] = useState(endParsed.date);
  const [endHour, setEndHour] = useState(endParsed.hour || '10');
  const [endMinute, setEndMinute] = useState(endParsed.minute || '00');
  
  const [saving, setSaving] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Sync formData.patientId with selectedPatient
  useEffect(() => {
    if (selectedPatient) {
      setFormData(prev => ({ ...prev, patientId: selectedPatient.id }));
    }
  }, [selectedPatient]);

  // Auto-generate title from patient name and time
  useEffect(() => {
    if (!isEditing && selectedPatient && startHour && startMinute && endHour && endMinute) {
      const title = `${selectedPatient.fullName} - ${startHour}:${startMinute} às ${endHour}:${endMinute}`;
      setFormData(prev => ({ ...prev, title }));
    } else if (!isEditing && !selectedPatient && startHour && startMinute && endHour && endMinute) {
      // Generate title without patient name
      const title = `Consulta - ${startHour}:${startMinute} às ${endHour}:${endMinute}`;
      setFormData(prev => ({ ...prev, title }));
    }
  }, [selectedPatient, startHour, startMinute, endHour, endMinute, isEditing]);

  // Convert local date/time to ISO string (Brazil timezone)
  const toISOString = (date: string, hour: string, minute: string): string => {
    if (!date || !hour || !minute) return '';
    const localDateTime = new Date(`${date}T${hour}:${minute}:00`);
    return localDateTime.toISOString();
  };

  // Update formData when individual fields change
  const updateStartTime = (date?: string, hour?: string, minute?: string) => {
    const newDate = date ?? startDate;
    const newHour = hour ?? startHour;
    const newMinute = minute ?? startMinute;
    
    if (newDate && newHour && newMinute) {
      const startISO = toISOString(newDate, newHour, newMinute);
      setFormData(prev => ({ ...prev, startUtc: startISO }));
      
      // Auto-adjust end time if it's empty or before start
      if (!endDate || !endHour || !endMinute) {
        const endDateTime = new Date(startISO);
        endDateTime.setHours(endDateTime.getHours() + 1);
        setEndDate(endDateTime.toISOString().split('T')[0]);
        setEndHour(String(endDateTime.getHours()).padStart(2, '0'));
        setEndMinute(String(endDateTime.getMinutes()).padStart(2, '0'));
        setFormData(prev => ({ ...prev, endUtc: endDateTime.toISOString() }));
      }
    }
  };

  const updateEndTime = (date?: string, hour?: string, minute?: string) => {
    const newDate = date ?? endDate;
    const newHour = hour ?? endHour;
    const newMinute = minute ?? endMinute;
    
    if (newDate && newHour && newMinute) {
      const endISO = toISOString(newDate, newHour, newMinute);
      setFormData(prev => ({ ...prev, endUtc: endISO }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clinicId || !formData.doctorId || !formData.title || !formData.startUtc || !formData.endUtc) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Final validation: end must be after start
    if (new Date(formData.endUtc) <= new Date(formData.startUtc)) {
      toast.error('Hora de término deve ser maior que a hora de início');
      return;
    }

    try {
      setSaving(true);

      const cleanData = {
        ...formData,
        patientId: formData.patientId || undefined,
        description: formData.description || undefined,
      };

      if (isEditing && appointment) {
        await appointmentsService.update(appointment.id, cleanData);
        toast.success('Agendamento atualizado com sucesso');
      } else {
        await appointmentsService.create(cleanData as CreateAppointmentData);
        toast.success('Agendamento criado com sucesso');
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar agendamento');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!appointment) return;
    await appointmentsService.cancel(appointment.id, reason);
  };

  const handleReschedule = async (data: { reason: string; newStartUtc: string; newEndUtc: string }) => {
    if (!appointment) return;
    await appointmentsService.reschedule(appointment.id, data);
  };

  return (
    <>
      <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-6">
          <h2 className="text-xl font-bold mb-4">
            {isEditing ? 'Editar Agendamento' : 'Novo Agendamento'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                rows={3}
              />
            </div>

            {/* Clinic and Doctor - Display Only */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clínica
                </label>
                <input
                  type="text"
                  value={clinic?.name || 'Clínica não selecionada'}
                  readOnly
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Médico
                </label>
                <input
                  type="text"
                  value={currentDoctorName || 'Médico'}
                  readOnly
                  className="w-full border rounded-lg px-3 py-2 bg-gray-50 cursor-not-allowed text-gray-600"
                />
              </div>
            </div>

            {/* Patient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paciente
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.patientId || ''}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  className="flex-1 border rounded-lg px-3 py-2"
                >
                  <option value="">Selecione (opcional)</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.fullName}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onPatientSearch}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 whitespace-nowrap"
                >
                  Buscar
                </button>
              </div>
            </div>

            {/* Start and End Time */}
            <div className="grid grid-cols-2 gap-4">
              {/* Start Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data/Hora Início *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      updateStartTime(e.target.value);
                    }}
                    className="col-span-3 border rounded-lg px-3 py-2"
                    required
                  />
                  <select
                    value={startHour}
                    onChange={(e) => {
                      setStartHour(e.target.value);
                      updateStartTime(undefined, e.target.value);
                    }}
                    className="border rounded-lg px-2 py-2"
                    required
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = String(i).padStart(2, '0');
                      return (
                        <option key={hour} value={hour}>
                          {hour}h
                        </option>
                      );
                    })}
                  </select>
                  <select
                    value={startMinute}
                    onChange={(e) => {
                      setStartMinute(e.target.value);
                      updateStartTime(undefined, undefined, e.target.value);
                    }}
                    className="col-span-2 border rounded-lg px-2 py-2"
                    required
                  >
                    {['00', '15', '30', '45'].map((min) => (
                      <option key={min} value={min}>
                        {min} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* End Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data/Hora Fim *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      updateEndTime(e.target.value);
                    }}
                    className="col-span-3 border rounded-lg px-3 py-2"
                    required
                  />
                  <select
                    value={endHour}
                    onChange={(e) => {
                      setEndHour(e.target.value);
                      updateEndTime(undefined, e.target.value);
                    }}
                    className="border rounded-lg px-2 py-2"
                    required
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = String(i).padStart(2, '0');
                      return (
                        <option key={hour} value={hour}>
                          {hour}h
                        </option>
                      );
                    })}
                  </select>
                  <select
                    value={endMinute}
                    onChange={(e) => {
                      setEndMinute(e.target.value);
                      updateEndTime(undefined, undefined, e.target.value);
                    }}
                    className="col-span-2 border rounded-lg px-2 py-2"
                    required
                  >
                    {['00', '15', '30', '45'].map((min) => (
                      <option key={min} value={min}>
                        {min} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* All Day */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isAllDay}
                  onChange={(e) => setFormData({ ...formData, isAllDay: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Dia inteiro</span>
              </label>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              {isEditing && appointment && appointment.status !== 'Cancelada' && appointment.status !== 'Realizada' && (
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={saving}
                >
                  Cancelar Consulta
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

      {/* Cancel/Reschedule Dialog */}
      {showCancelDialog && appointment && (
        <CancelRescheduleDialog
          appointmentId={appointment.id}
          appointmentTitle={appointment.title}
          onClose={() => setShowCancelDialog(false)}
          onSuccess={() => {
            setShowCancelDialog(false);
            onSuccess();
          }}
          onCancel={handleCancel}
          onReschedule={handleReschedule}
        />
      )}
    </>
  );
}

// Delete Confirmation Dialog Component
interface DeleteConfirmDialogProps {
  appointmentTitle: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmDialog({
  appointmentTitle,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-white/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
        <h2 className="text-xl font-bold mb-4">Confirmar Exclusão</h2>
        <p className="text-gray-600 mb-6">
          Tem certeza que deseja excluir o agendamento <strong>{appointmentTitle}</strong>?
          Esta ação não pode ser desfeita.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
}

export default CalendarPage;
